import { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'

import usePlacowkiData from './hooks/usePlacowkiData'
import usePlacowkiModal from './hooks/usePlacowkiModal'
import usePlacowkiSelection from './hooks/usePlacowkiSelection'
import KampanieService from '@/services/KampanieService'
import KampaniePlacowkiService from '@/services/KampaniePlacowkiService'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import PlacowkiTable from './components/PlacowkiTable'
import PlacowkiModal from './components/PlacowkiModal'
import DodajKampanieModal from './components/DodajKampanieModal'
import MiastoFilterModal from '../../../../filter-modals/components/MiastoFilterModal'
import GminaFilterModal from '../../../../filter-modals/components/GminaFilterModal'
import PowiatFilterModal from '../../../../filter-modals/components/PowiatFilterModal'
import WojewodztwoFilterModal from '../../../../filter-modals/components/WojewodztwoFilterModal'
import TypFilterModal from '../../../../filter-modals/components/TypFilterModal'
import StatusFilterModal from '../../../../filter-modals/components/StatusFilterModal'
import { TRIAGE_OPTIONS } from '../common/triageCategories'

export default function Placowki() {
	const [isMiastoFilterOpen, setIsMiastoFilterOpen] = useState(false)
	const [isGminaFilterOpen, setIsGminaFilterOpen] = useState(false)
	const [isPowiatFilterOpen, setIsPowiatFilterOpen] = useState(false)
	const [isWojewodztwoFilterOpen, setIsWojewodztwoFilterOpen] = useState(false)
	const [isTypFilterOpen, setIsTypFilterOpen] = useState(false)
	const [isTriageFilterOpen, setIsTriageFilterOpen] = useState(false)
	const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false)
	const [campaignPlacowki, setCampaignPlacowki] = useState([])
	const [campaignMode, setCampaignMode] = useState('selected')
	const [campaignRecordCount, setCampaignRecordCount] = useState(0)
	const [campaignCountLoading, setCampaignCountLoading] = useState(false)
	const [campaignSaving, setCampaignSaving] = useState(false)

	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		searchQuery,
		filters,
		clearError,
	} = usePlacowkiData()
	const {
		isModalOpen,
		editingItem,
		formName,
		setFormName,
		formAdres,
		setFormAdres,
		formMiastoId,
		formMiastoName,
		formGminaId,
		formGminaName,
		formPowiatId,
		formPowiatName,
		formWojewodztwoId,
		formWojewodztwoName,
		formTypId,
		formTypName,
		formNip,
		setFormNip,
		isSaving,
		isMiastoSelectOpen,
		isGminaSelectOpen,
		isPowiatSelectOpen,
		isWojewodztwoSelectOpen,
		isTypSelectOpen,
		openEditModal,
		handleDelete,
		closeModal,
		handleSubmit,
		openMiastoSelectModal,
		closeMiastoSelectModal,
		applyMiastoSelection,
		openGminaSelectModal,
		closeGminaSelectModal,
		applyGminaSelection,
		openPowiatSelectModal,
		closePowiatSelectModal,
		applyPowiatSelection,
		openWojewodztwoSelectModal,
		closeWojewodztwoSelectModal,
		applyWojewodztwoSelection,
		openTypSelectModal,
		closeTypSelectModal,
		applyTypSelection,
		buttons: modalButtons,
	} = usePlacowkiModal(loadData)
	const {
		selectedPlacowki,
		handleSelectAll,
		handleSelectPlacowka,
		getSelectionState,
	} = usePlacowkiSelection()

	const triageFilterOptions = useMemo(
		() => TRIAGE_OPTIONS.map(option => ({ value: option.value, label: option.label })),
		[],
	)

	const selectedMiastoId = filters.id_miasto || null
	const selectedGminaId = filters.id_gmina || null
	const selectedPowiatId = filters.id_powiat || null
	const selectedWojewodztwoId = filters.id_wojewodztwo || null
	const selectedTypId = filters.id_typ || null
	const selectedTriage = filters.triage ?? null

	const hasTriageFilter =
		selectedTriage !== null && selectedTriage !== undefined && selectedTriage !== ''

	const activeFiltersCount = useMemo(() => {
		const filterValues = [
			selectedMiastoId,
			selectedGminaId,
			selectedPowiatId,
			selectedWojewodztwoId,
			selectedTypId,
		]
		const baseCount = filterValues.filter(Boolean).length
		return hasTriageFilter ? baseCount + 1 : baseCount
	}, [
		selectedMiastoId,
		selectedGminaId,
		selectedPowiatId,
		selectedWojewodztwoId,
		selectedTypId,
		hasTriageFilter,
	])

	const buildCampaignFilterOptions = useCallback(
		() => ({
			searchText: filters.searchText || '',
			filters: {
				id_miasto: filters.id_miasto || null,
				id_gmina: filters.id_gmina || null,
				id_powiat: filters.id_powiat || null,
				id_wojewodztwo: filters.id_wojewodztwo || null,
				id_typ: filters.id_typ || null,
				triage:
					filters.triage !== null && filters.triage !== undefined && filters.triage !== ''
						? filters.triage
						: null,
			},
		}),
		[filters],
	)

	const openCampaignModal = useCallback((mode, placowki = [], recordCount = 0) => {
		setCampaignMode(mode)
		setCampaignPlacowki(Array.isArray(placowki) ? placowki : [])
		setCampaignRecordCount(recordCount)
		setIsCampaignModalOpen(true)
	}, [])

	const closeCampaignModal = useCallback(() => {
		setIsCampaignModalOpen(false)
		setCampaignPlacowki([])
		setCampaignMode('selected')
		setCampaignRecordCount(0)
		setCampaignCountLoading(false)
		setCampaignSaving(false)
	}, [])

	const handleAddSelectedToCampaign = useCallback(() => {
		const selectedData = data.filter(item => selectedPlacowki.has(item?._id || item?.id))
		openCampaignModal('selected', selectedData, selectedData.length)
	}, [data, openCampaignModal, selectedPlacowki])

	const handleAddFilterToCampaign = useCallback(async () => {
		setCampaignCountLoading(true)
		openCampaignModal('filter', [], totalCount)

		const result = await KampaniePlacowkiService.addFromFilterCount(buildCampaignFilterOptions())
		setCampaignRecordCount(result.count ?? totalCount)
		setCampaignCountLoading(false)
	}, [buildCampaignFilterOptions, openCampaignModal, totalCount])

	const handleCampaignSave = useCallback(async payload => {
		setCampaignSaving(true)

		const filterOptions = buildCampaignFilterOptions()

		let result
		if (payload.target === 'existing') {
			if (campaignMode === 'filter') {
				result = await KampaniePlacowkiService.addToCampaignFromFilter(
					payload.kampaniaId,
					filterOptions,
				)
			} else {
				const placowkaIds = campaignPlacowki
					.map(item => item?._id ?? item?.id)
					.filter(Boolean)
				result = await KampaniePlacowkiService.addToCampaign(payload.kampaniaId, placowkaIds)
			}
		} else if (campaignMode === 'filter') {
			result = await KampanieService.createWithFilter(
				{ nazwa: payload.nazwa, description: payload.description },
				filterOptions,
			)
		} else {
			result = await KampanieService.createFromPlacowki(
				{ nazwa: payload.nazwa, description: payload.description },
				campaignPlacowki,
			)
		}

		setCampaignSaving(false)

		if (!result.success) {
			Swal.fire({
				title: 'Error',
				text: result.error || 'Nie udalo sie dodac do kampanii',
				icon: 'error',
			})
			return
		}

		if (payload.target === 'existing') {
			const placowkiAdded = result.data?.placowkiAdded ?? campaignRecordCount
			const skipped = result.data?.skipped ?? 0
			const skippedText = skipped > 0 ? ` (${skipped} pominietych)` : ''

			Swal.fire({
				title: 'Sukces',
				text: `Dodano ${placowkiAdded} placowek do kampanii "${payload.kampaniaName || ''}"${skippedText}`,
				icon: 'success',
			})
		} else {
			const placowkiAdded =
				campaignMode === 'filter'
					? result.data?.placowkiAdded ?? campaignRecordCount
					: campaignPlacowki.length

			Swal.fire({
				title: 'Sukces',
				text: `Kampania zostala dodana (${placowkiAdded} placowek)`,
				icon: 'success',
			})
		}

		closeCampaignModal()
	}, [campaignMode, campaignPlacowki, campaignRecordCount, closeCampaignModal, buildCampaignFilterOptions])

	const { isAllSelected, isIndeterminate } = getSelectionState(data)

	const buttons = useMemo(
		() => [
			{
				type: 'clear-filters',
				label: `Wyczysc filtry(${activeFiltersCount})`,
				onClick: () =>
					updateFilters({
						id_miasto: null,
						id_gmina: null,
						id_powiat: null,
						id_wojewodztwo: null,
						id_typ: null,
						triage: null,
					}),
				disabled: activeFiltersCount === 0,
				className:
					'px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center',
			},
			{
				type: 'campaign-selected',
				label: 'Zaznaczone do kampanii',
				onClick: handleAddSelectedToCampaign,
				disabled: selectedPlacowki.size === 0,
			},
			{
				type: 'campaign-filter',
				label: 'Filtr do kampanii',
				onClick: handleAddFilterToCampaign,
				disabled: data.length === 0,
			},
			...modalButtons,
		],
		[
			activeFiltersCount,
			data,
			handleAddFilterToCampaign,
			handleAddSelectedToCampaign,
			modalButtons,
			selectedPlacowki,
			updateFilters,
		],
	)

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: 'Error',
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		updateFilters({ searchText: e.target.value })
	}

	const handleApplyMiastoFilter = miastoId => {
		updateFilters({ id_miasto: miastoId || null })
		setIsMiastoFilterOpen(false)
	}

	const handleApplyGminaFilter = gminaId => {
		const nextGminaId = gminaId || null
		updateFilters({
			id_gmina: nextGminaId,
			...(nextGminaId !== selectedGminaId && { id_miasto: null }),
		})
		setIsGminaFilterOpen(false)
	}

	const handleApplyPowiatFilter = powiatId => {
		const nextPowiatId = powiatId || null
		updateFilters({
			id_powiat: nextPowiatId,
			...(nextPowiatId !== selectedPowiatId && {
				id_gmina: null,
				id_miasto: null,
			}),
		})
		setIsPowiatFilterOpen(false)
	}

	const handleApplyWojewodztwoFilter = wojewodztwoId => {
		const nextWojewodztwoId = wojewodztwoId || null
		updateFilters({
			id_wojewodztwo: nextWojewodztwoId,
			...(nextWojewodztwoId !== selectedWojewodztwoId && {
				id_powiat: null,
				id_gmina: null,
				id_miasto: null,
			}),
		})
		setIsWojewodztwoFilterOpen(false)
	}

	const handleApplyTypFilter = typId => {
		updateFilters({ id_typ: typId || null })
		setIsTypFilterOpen(false)
	}

	const handleApplyTriageFilter = triage => {
		const nextTriage =
			triage !== null && triage !== undefined && triage !== '' ? Number(triage) : null
		updateFilters({ triage: nextTriage })
		setIsTriageFilterOpen(false)
	}

	const onSelectAll = checked => {
		handleSelectAll(checked, data)
	}

	return (
		<>
			<div className="flex h-[calc(100dvh-6rem)] flex-col lg:h-[calc(100dvh-2.5rem)]">
				<SearchActionsBar
					value={searchQuery}
					onChange={handleSearchChange}
					placeholder="Szukaj placowek po nazwie..."
					buttons={buttons}
				/>

				<PlacowkiTable
				data={data}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				onEdit={openEditModal}
				onDelete={handleDelete}
				selectedPlacowki={selectedPlacowki}
				onSelectAll={onSelectAll}
				onSelectPlacowka={handleSelectPlacowka}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				hasMiastoFilter={!!selectedMiastoId}
				hasGminaFilter={!!selectedGminaId}
				hasPowiatFilter={!!selectedPowiatId}
				hasWojewodztwoFilter={!!selectedWojewodztwoId}
				hasTypFilter={!!selectedTypId}
				hasTriageFilter={hasTriageFilter}
				onOpenMiastoFilter={() => setIsMiastoFilterOpen(true)}
				onOpenGminaFilter={() => setIsGminaFilterOpen(true)}
				onOpenPowiatFilter={() => setIsPowiatFilterOpen(true)}
				onOpenWojewodztwoFilter={() => setIsWojewodztwoFilterOpen(true)}
				onMiastoFilter={handleApplyMiastoFilter}
				onGminaFilter={handleApplyGminaFilter}
				onPowiatFilter={handleApplyPowiatFilter}
				onWojewodztwoFilter={handleApplyWojewodztwoFilter}
				onTypFilter={handleApplyTypFilter}
				onOpenTypFilter={() => setIsTypFilterOpen(true)}
				onOpenTriageFilter={() => setIsTriageFilterOpen(true)}
				onTriageFilter={handleApplyTriageFilter}
				/>

				<LoadMoreSection
					hasMore={hasMore}
					loadingMore={loadingMore}
					onLoadMore={handleLoadMore}
					dataLength={data.length}
					totalCount={totalCount}
				/>
			</div>
			<PlacowkiModal
				isOpen={isModalOpen}
				editingItem={editingItem}
				formName={formName}
				setFormName={setFormName}
				formAdres={formAdres}
				setFormAdres={setFormAdres}
				formMiastoId={formMiastoId}
				formMiastoName={formMiastoName}
				onOpenMiastoSelect={openMiastoSelectModal}
				formGminaId={formGminaId}
				formGminaName={formGminaName}
				onOpenGminaSelect={openGminaSelectModal}
				formPowiatId={formPowiatId}
				formPowiatName={formPowiatName}
				onOpenPowiatSelect={openPowiatSelectModal}
				formWojewodztwoId={formWojewodztwoId}
				formWojewodztwoName={formWojewodztwoName}
				onOpenWojewodztwoSelect={openWojewodztwoSelectModal}
				formTypId={formTypId}
				formTypName={formTypName}
				onOpenTypSelect={openTypSelectModal}
				formNip={formNip}
				setFormNip={setFormNip}
				isSaving={isSaving}
				onClose={closeModal}
				onSubmit={handleSubmit}
			/>
			<MiastoFilterModal
				isOpen={isMiastoFilterOpen}
				onClose={() => setIsMiastoFilterOpen(false)}
				selectedMiastoId={selectedMiastoId}
				idGmina={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyMiastoFilter}
			/>
			<GminaFilterModal
				isOpen={isGminaFilterOpen}
				onClose={() => setIsGminaFilterOpen(false)}
				selectedGminaId={selectedGminaId}
				idPowiat={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyGminaFilter}
			/>
			<PowiatFilterModal
				isOpen={isPowiatFilterOpen}
				onClose={() => setIsPowiatFilterOpen(false)}
				selectedPowiatId={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyPowiatFilter}
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoFilterOpen}
				onClose={() => setIsWojewodztwoFilterOpen(false)}
				selectedWojewodztwoId={selectedWojewodztwoId}
				onApply={handleApplyWojewodztwoFilter}
			/>
			<TypFilterModal
				isOpen={isTypFilterOpen}
				onClose={() => setIsTypFilterOpen(false)}
				selectedTypId={selectedTypId}
				onApply={handleApplyTypFilter}
			/>
			<StatusFilterModal
				isOpen={isTriageFilterOpen}
				onClose={() => setIsTriageFilterOpen(false)}
				selectedStatus={selectedTriage}
				onApply={handleApplyTriageFilter}
				statusOptions={triageFilterOptions}
				title="Filtruj po kategorii klienta"
				description="Wybierz kategorie klienta, aby zawezic liste placowek."
				applyLabel="Zastosuj"
				clearLabel="Wyczysc filtr"
			/>
			<MiastoFilterModal
				isOpen={isMiastoSelectOpen}
				onClose={closeMiastoSelectModal}
				selectedMiastoId={formMiastoId}
				idGmina={formGminaId}
				idPowiat={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyMiastoSelection}
				title="Wybierz miasto"
				description="Wyszukaj i wybierz miasto dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<GminaFilterModal
				isOpen={isGminaSelectOpen}
				onClose={closeGminaSelectModal}
				selectedGminaId={formGminaId}
				idPowiat={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyGminaSelection}
				title="Wybierz gmine"
				description="Wyszukaj i wybierz gmine dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<PowiatFilterModal
				isOpen={isPowiatSelectOpen}
				onClose={closePowiatSelectModal}
				selectedPowiatId={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyPowiatSelection}
				title="Wybierz powiat"
				description="Wyszukaj i wybierz powiat dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoSelectOpen}
				onClose={closeWojewodztwoSelectModal}
				selectedWojewodztwoId={formWojewodztwoId}
				onApply={applyWojewodztwoSelection}
				title="Wybierz wojewodztwo"
				description="Wyszukaj i wybierz wojewodztwo dla placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<TypFilterModal
				isOpen={isTypSelectOpen}
				onClose={closeTypSelectModal}
				selectedTypId={formTypId}
				onApply={applyTypSelection}
				title="Wybierz typ placowki"
				description="Wyszukaj i wybierz typ placowki."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<DodajKampanieModal
				isOpen={isCampaignModalOpen}
				onClose={closeCampaignModal}
				recordCount={campaignRecordCount}
				countLoading={campaignCountLoading}
				isSaving={campaignSaving}
				onSave={handleCampaignSave}
			/>
		</>
	)
}
